import React, { FC, useEffect, useState } from 'react';
import Speakers from '../speakers/Speakers';
import Programs from '../programs/Programs';
import { getApiService, getStorageService } from 'services';
import { getProgramApi, getSpeakersApiService } from 'services/api.factory';
import { PaginationInput, PaginationOutput } from 'kl-b2c-ui-kit';
import { DateSession, ProgramSession, ProgramTag, SelectedDates, Speaker, Tag } from 'types';

const BASE_FILTERS = { page: 0, size: 8, tagIds: [], pristine: true };

interface EventsDictionary {
    [key: string]: EventsDictionaryItem;
}

interface EventsDictionaryItem {
    date: string | null;
    eventId: string;
}

const ConferenceWrapper: FC<{ conferenceId?: string }> = ({ conferenceId }) => {
    const storageService = getStorageService();
    const apiService = getApiService(storageService);
    const speakersApiService = getSpeakersApiService(apiService);
    const programsApiService = getProgramApi(apiService);

    const [init, setInit] = useState(true);
    const [speakers, setSpeakers] = useState<PaginationOutput<Speaker> | null>(null);
    const [speakersTags, setSpeakersTags] = useState<Tag[] | null>(null);
    const [programTags, setProgramTags] = useState<ProgramTag[]>([]);
    const [config, setConfig] = useState<PaginationInput & { tagIds: string[]; pristine?: boolean }>(BASE_FILTERS);
    const [isFilterDirty, setIsFilterDirty] = useState<boolean>(false);
    const [eventsDictionary, setEventsDictionary] = useState<EventsDictionary | null>(null);
    const [eventsIds, setEventsIds] = useState<string[] | null>(null);
    const [sessions, setSessions] = useState<DateSession[]>([]);
    const [selectedTags, setSelectedTags] = useState<string[]>([]);
    const [selectedDate, setSelectedDate] = useState<SelectedDates[]>([]);
    const [currentSessions, setCurrentSessions] = useState<ProgramSession[]>();
    const [openedIds, setOpenedIds] = useState<Set<string>>(new Set());

    const getEvents = (id: string, sessions: DateSession[]): EventsDictionaryItem | null => {
        const filtered = sessions.filter((s) =>
            s.sessions.some(
                (sessions) =>
                    sessions.programEvents.length > 0 && sessions.programEvents.some((event) => event.id === id)
            )
        );

        if (filtered?.length) {
            return {
                date: filtered[0].date,
                eventId: id,
            };
        }

        return null;
    };

    const onSetOpenedIds = (id: string) => {
        const newOpenedIds = new Set(Array.from(openedIds));
        if (openedIds.has(id)) {
            newOpenedIds.delete(id);
        } else {
            newOpenedIds.add(id);
        }

        setOpenedIds(newOpenedIds);
    };

    const onSetOpenedIdsFromSpeaker = (id: string) => {
        const newOpenedIds = new Set(Array.from(openedIds));
        newOpenedIds.add(id);

        if (eventsDictionary && eventsDictionary[id]) {
            const newDates = selectedDate.map((d) => {
                if (d.date === eventsDictionary[id].date) {
                    return {
                        ...d,
                        isSelected: true,
                    };
                }

                return {
                    ...d,
                    isSelected: false,
                };
            });

            setSelectedTags([]);

            const selectedIndex = newDates.findIndex((r) => r.isSelected);
            setCurrentSessions(selectedIndex >= 0 ? sessions[selectedIndex].sessions : []);

            setSelectedDate(newDates);

            setTimeout(() => {
                const eventBlock = document.getElementById(id);
                eventBlock?.scrollIntoView();
                setOpenedIds(newOpenedIds);
            }, 100);
        }
    };

    useEffect(() => {
        const { page, size, tagIds, pristine } = config;

        speakersApiService.getAll({ page, size, tagIds }).then((response) => {
            if (pristine) {
                setSpeakers(response.data);

                const { hash } = window.location;

                const elementToScroll = document.getElementById(hash?.replace('#', ''));

                if (!elementToScroll || hash !== '#speakers') return;

                window.scrollTo({
                    top: elementToScroll.offsetTop,
                });

                return;
            }

            let newItems: Speaker[] = [];

            if (speakers?.items) {
                newItems = [...speakers.items];
            }

            if (response.data?.items) {
                newItems = [...newItems, ...response.data.items];
            }

            setSpeakers({ count: response.data.count, items: newItems });
        });
    }, [config]);

    useEffect(() => {
        if (speakers && speakers?.items?.length) {
            const speakersEvents = speakers?.items.map((speaker) => speaker.defaultEvent?.id);
            setEventsIds(speakersEvents);
        }
    }, [speakers]);

    useEffect(() => {
        if (eventsIds && sessions && sessions.length) {
            const eventsDictionary = Object.assign(
                {},
                ...eventsIds.map((x) => ({
                    [x]: getEvents(x, sessions),
                }))
            );

            setEventsDictionary(eventsDictionary);
        }
    }, [eventsIds, sessions]);

    useEffect(() => {
        if (init) return;

        const newSessions = [
            ...sessions.filter(
                (innerSession) =>
                    !selectedTags.length ||
                    innerSession.sessions.some((is) =>
                        is.programEvents.some((ie) => ie.tags.some((it) => selectedTags.includes(it.id)))
                    )
            ),
        ];
        const oldSelectedDate = selectedDate.find((r) => r.isSelected);
        const newDates: SelectedDates[] = [
            ...newSessions.map((m) => {
                const isCurrent = oldSelectedDate?.date === m.date;
                return {
                    date: m.date,
                    isSelected: isCurrent,
                };
            }),
        ];
        if (newDates.length && newDates.findIndex((r) => r.isSelected) <= 0) {
            newDates[0].isSelected = true;
        }
        const selectedIndex = newDates.findIndex((r) => r.isSelected);
        setCurrentSessions(selectedIndex >= 0 ? newSessions[selectedIndex].sessions : []);
        setSelectedDate(newDates);
    }, [selectedTags]);

    useEffect(() => {
        speakersApiService.getTags({ page: 0, size: 100 }).then((response) => setSpeakersTags(response.data.items));

        programsApiService
            .getSettings(conferenceId)
            .then((data) => {
                setProgramTags(data?.data?.tags ?? []);
                setSelectedDate(
                    data?.data?.sessions?.map((d, i) => {
                        if (i === 0) setCurrentSessions(d.sessions);
                        return {
                            date: d.date,
                            isSelected: i === 0,
                        };
                    }) ?? []
                );

                setSessions(data?.data?.sessions ?? []);
            })
            .finally(() => {
                setInit(false);

                setTimeout(() => {
                    const { hash } = window.location;

                    const elementToScroll = document.getElementById(hash?.replace('#', ''));

                    if (!elementToScroll || hash !== '#programs') return;

                    window.scrollTo({
                        top: elementToScroll.offsetTop,
                    });
                }, 500);
            });
    }, [conferenceId]);

    const showSpeakers = !!conferenceId && !!speakers?.items?.length && !!speakersTags?.length;
    const showPrograms = !!sessions.length && !!programTags.length && !!currentSessions;

    return (
        <>
            {showSpeakers && (
                <Speakers
                    tags={speakersTags}
                    speakers={speakers}
                    config={config}
                    setConfig={setConfig}
                    setIsDirty={setIsFilterDirty}
                    setOpenedIds={onSetOpenedIdsFromSpeaker}
                />
            )}
            {showPrograms && (
                <Programs
                    selectedTags={selectedTags}
                    tags={programTags}
                    currentSessions={currentSessions}
                    selectedDate={selectedDate}
                    sessions={sessions}
                    setCurrentSessions={setCurrentSessions}
                    setSelectedDate={setSelectedDate}
                    setSelectedTags={setSelectedTags}
                    openedIds={openedIds}
                    setOpened={onSetOpenedIds}
                />
            )}
        </>
    );
};

export default ConferenceWrapper;
